/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: components\CrmConfigurationTab.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the Integrations Config module.
----------------------------------------------------------------*/


import { Settings, Lock, RefreshCw, Copy } from 'lucide-react';
import { FormBox } from '../../../components/ui/FormBox';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import Toggle from '../../../components/ui/Toggle';
import SimpleTooltip from '../../../components/ui/SimpleTooltip';
import { TimezoneSelect } from '../../../components/ui/TimezoneSelect';
import { Button } from '../../../components/ui/button';

interface CrmFieldConfig {
    authTypes: string[];
    requiresServerUrl: boolean;
    requiresTenantId: boolean;
    requiresDataCenter: boolean;
    requiresDatabaseName: boolean;
    requiresTimezone?: boolean;
    serverUrlLabel?: string;
    serverUrlPlaceholder?: string;
    serverUrlHelp?: string;
}

interface ZohoDataCenter {
    value: string;
    label: string;
}

interface CrmConfig {
    name: string;
    enabled: boolean;
    authType: string;
    clientId: string;
    clientSecret: string;
    serverUrl: string;
    apiKey: string;
    pathToken: string;
    syncContacts: boolean;
    syncCalls: boolean;
    tenantId: string;
    dataCenter: string;
    databaseName: string;
    timezone: string;
}

interface CrmConfigurationTabProps {
    providerId: string;
    config: CrmConfig;
    onConfigChange: (config: CrmConfig) => void;
    currentCrmConfig: CrmFieldConfig;
    zohoDataCenters: ZohoDataCenter[];
    redirectUri: string;
    isConnected: boolean;
    isSaving: boolean;
    onConnect: () => void;
    onConnectWithToken: () => void;
    t: (key: string, fallback?: string) => string;
}

export function CrmConfigurationTab({
    providerId, config, onConfigChange, currentCrmConfig, zohoDataCenters,
    redirectUri, isConnected, isSaving, onConnect, onConnectWithToken, t,
}: CrmConfigurationTabProps) {
    const isOauth = config.authType === 'oauth2';
    const needsServerUrl = currentCrmConfig.requiresServerUrl;

    const set = (partial: Partial<CrmConfig>) => onConfigChange({ ...config, ...partial });

    return (
        <div className="space-y-6">
            {/* General Settings */}
            <FormBox title={t('crm.configuration.generalSettings')} icon={<Settings className="h-5 w-5" />}>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-3 items-center p-6">
                    {/* Connection Name */}
                    <SimpleTooltip content={t('integrations.connectionName')}>
                        <Label htmlFor="connectionName" className="text-left">{t('integrations.connectionName')}</Label>
                    </SimpleTooltip>
                    <Input
                        id="connectionName"
                        value={config.name}
                        onChange={(e) => set({ name: e.target.value })}
                        placeholder={t('integrations.connectionNamePlaceholder') || 'Default'}
                    />

                    {/* Auth Type */}
                    <SimpleTooltip content={t('crm.configuration.fields.authType')}>
                        <Label htmlFor="authType" className="text-left">{t('crm.configuration.fields.authType')}</Label>
                    </SimpleTooltip>
                    <select
                        id="authType"
                        value={config.authType}
                        onChange={(e) => set({ authType: e.target.value })}
                        className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                    >
                        <option value="oauth2">{t('crm.configuration.options.oauth2')}</option>
                        <option value="api_key">{t('crm.configuration.options.apiKey')}</option>
                    </select>

                    {/* Enabled */}
                    <SimpleTooltip content={t('crm.configuration.fields.enabled')}>
                        <Label htmlFor="enabled" className="text-left">{t('crm.configuration.fields.enabled')}</Label>
                    </SimpleTooltip>
                    <Toggle value={config.enabled} onChange={(val) => set({ enabled: val })} />

                    {/* Timezone � Calendly only */}
                    {providerId === 'calendly' && (
                        <>
                            <SimpleTooltip content={`${t('integrations.calendly.timezoneHelp') || 'Timezone for scheduling and displaying appointment times'}. ${t('integrations.calendly.timezoneDescription') || 'Used for converting appointment times when communicating with AI'}`}>
                                <Label htmlFor="timezone" className="text-left">
                                    {t('integrations.calendly.timezone') || 'Timezone'}
                                </Label>
                            </SimpleTooltip>
                            <TimezoneSelect
                                value={config.timezone || 'America/Guatemala'}
                                onChange={(value) => set({ timezone: value })}
                                placeholder={t('integrations.calendly.selectTimezone') || 'Select timezone...'}
                            />
                        </>
                    )}
                </div>
            </FormBox>

            {/* Authentication Credentials */}
            <FormBox title={t('crm.configuration.authentication')} icon={<Lock className="h-5 w-5" />}>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-3 items-center p-6">
                    {isOauth ? (
                        <>
                            <SimpleTooltip content={t('crm.configuration.fields.clientId')}>
                                <Label htmlFor="clientId" className="text-left">{t('crm.configuration.fields.clientId')}</Label>
                            </SimpleTooltip>
                            <Input
                                id="clientId"
                                value={config.clientId}
                                onChange={(e) => set({ clientId: e.target.value })}
                                placeholder={t('crm.configuration.placeholders.clientId')}
                            />

                            <SimpleTooltip content={t('crm.configuration.fields.clientSecret')}>
                                <Label htmlFor="clientSecret" className="text-left">{t('crm.configuration.fields.clientSecret')}</Label>
                            </SimpleTooltip>
                            <Input
                                id="clientSecret"
                                type="password"
                                value={config.clientSecret}
                                onChange={(e) => set({ clientSecret: e.target.value })}
                                placeholder={t('crm.configuration.placeholders.clientSecret')}
                            />

                            {needsServerUrl && (
                                <>
                                    <SimpleTooltip content={currentCrmConfig.serverUrlHelp || t('crm.configuration.fields.serverUrl')}>
                                        <Label htmlFor="serverUrl" className="text-left">
                                            {currentCrmConfig.serverUrlLabel || t('crm.configuration.fields.serverUrl')}
                                        </Label>
                                    </SimpleTooltip>
                                    <div className="col-span-3">
                                        <Input
                                            id="serverUrl"
                                            value={config.serverUrl}
                                            onChange={(e) => set({ serverUrl: e.target.value })}
                                            placeholder={currentCrmConfig.serverUrlPlaceholder || t('crm.configuration.placeholders.serverUrl')}
                                        />
                                        {currentCrmConfig.serverUrlHelp && (
                                            <p className="text-xs text-muted-foreground mt-1">{currentCrmConfig.serverUrlHelp}</p>
                                        )}
                                    </div>
                                </>
                            )}

                            {currentCrmConfig.requiresDataCenter && (
                                <>
                                    <SimpleTooltip content="Select your Zoho account's data center region">
                                        <Label htmlFor="dataCenter" className="text-left">Data Center Region</Label>
                                    </SimpleTooltip>
                                    <div className="col-span-3">
                                        <select
                                            id="dataCenter"
                                            value={config.dataCenter}
                                            onChange={(e) => set({ dataCenter: e.target.value })}
                                            className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                                        >
                                            {zohoDataCenters.map((dc) => (
                                                <option key={dc.value} value={dc.value}>{dc.label}</option>
                                            ))}
                                        </select>
                                        <p className="text-xs text-muted-foreground mt-1">
                                            Your Zoho account's data center determines the API endpoints used
                                        </p>
                                    </div>
                                </>
                            )}

                            {currentCrmConfig.requiresTenantId && (
                                <>
                                    <SimpleTooltip content="Azure Active Directory Tenant ID for your organization">
                                        <Label htmlFor="tenantId" className="text-left">Tenant ID</Label>
                                    </SimpleTooltip>
                                    <div className="col-span-3">
                                        <Input
                                            id="tenantId"
                                            value={config.tenantId}
                                            onChange={(e) => set({ tenantId: e.target.value })}
                                            placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                                        />
                                        <p className="text-xs text-muted-foreground mt-1">
                                            Found in Azure Portal ? Azure AD ? Overview ? Tenant ID
                                        </p>
                                    </div>
                                </>
                            )}

                            {currentCrmConfig.requiresDatabaseName && (
                                <>
                                    <SimpleTooltip content="The name of your Odoo database">
                                        <Label htmlFor="databaseName" className="text-left">Database Name</Label>
                                    </SimpleTooltip>
                                    <div className="col-span-3">
                                        <Input
                                            id="databaseName"
                                            value={config.databaseName}
                                            onChange={(e) => set({ databaseName: e.target.value })}
                                            placeholder="mycompany_production"
                                        />
                                        <p className="text-xs text-muted-foreground mt-1">
                                            Usually found in your Odoo URL or database manager
                                        </p>
                                    </div>
                                </>
                            )}

                            {/* Redirect URI */}
                            <SimpleTooltip content={t('crm.configuration.fields.redirectUri')}>
                                <Label htmlFor="redirectUri" className="text-left">{t('crm.configuration.fields.redirectUri')}</Label>
                            </SimpleTooltip>
                            <div className="col-span-3 flex gap-2">
                                <Input
                                    id="redirectUri"
                                    value={redirectUri}
                                    readOnly
                                    className="bg-muted text-muted-foreground font-mono text-xs"
                                />
                                <SimpleTooltip content={t('common.copy')}>
                                    <button
                                        type="button"
                                        onClick={() => navigator.clipboard.writeText(redirectUri)}
                                        className="p-2 bg-background border border-input rounded-md hover:bg-accent transition-colors"
                                    >
                                        <Copy className="h-4 w-4" />
                                    </button>
                                </SimpleTooltip>
                            </div>
                            <p className="text-xs text-muted-foreground">{t('crm.configuration.fields.redirectUriHelp')}</p>

                            <div className="col-start-2 col-span-3 mt-2">
                                <Button
                                    type="button"
                                    onClick={onConnect}
                                    disabled={isSaving || !config.clientId || !config.clientSecret}
                                >
                                    {t('crm.connectBtn', 'Connect with Provider')}
                                </Button>
                            </div>
                        </>
                    ) : (
                        <>
                            <SimpleTooltip content={t('crm.configuration.fields.apiKey')}>
                                <Label htmlFor="apiKey" className="text-left">{t('crm.configuration.fields.apiKey')}</Label>
                            </SimpleTooltip>
                            <div className="col-span-3">
                                <Input
                                    id="apiKey"
                                    type="password"
                                    value={config.apiKey}
                                    onChange={(e) => set({ apiKey: e.target.value })}
                                    placeholder={isConnected && !config.apiKey ? '����������������' : t('crm.configuration.placeholders.apiKey')}
                                />
                                {isConnected && !config.apiKey && (
                                    <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                                        <span className="w-2 h-2 rounded-full bg-green-500 inline-block"></span>
                                        {t('integrations.apiKeyConfigured') || 'API Key is already configured. Leave empty to keep current.'}
                                    </p>
                                )}
                                <p className="text-xs text-muted-foreground mt-1">
                                    {t('crm.configuration.apiKeyHelp', 'Enter your Personal Access Token from Calendly')}
                                </p>
                            </div>
                            <div className="col-start-2 col-span-3 mt-2">
                                <Button
                                    type="button"
                                    onClick={onConnectWithToken}
                                    disabled={isSaving || !config.apiKey}
                                >
                                    {isSaving ? t('common.connecting', 'Connecting...') : t('crm.connectBtn', 'Connect with Token')}
                                </Button>
                            </div>
                        </>
                    )}
                </div>
            </FormBox>

            {/* Synchronization Settings � not for Calendly */}
            {providerId !== 'calendly' && (
                <FormBox title={t('crm.configuration.syncSettings')} icon={<RefreshCw className="h-5 w-5" />}>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-3 items-center p-6">
                        <SimpleTooltip content={t('crm.configuration.syncContactsHelp')}>
                            <Label htmlFor="syncContacts" className="text-left">{t('crm.configuration.fields.syncContacts')}</Label>
                        </SimpleTooltip>
                        <Toggle value={config.syncContacts} onChange={(val) => set({ syncContacts: val })} />

                        <SimpleTooltip content={t('crm.configuration.syncCallsHelp')}>
                            <Label htmlFor="syncCalls" className="text-left">{t('crm.configuration.fields.syncCalls')}</Label>
                        </SimpleTooltip>
                        <Toggle value={config.syncCalls} onChange={(val) => set({ syncCalls: val })} />
                    </div>
                </FormBox>
            )}
        </div>
    );
}
